import { RequestPriority } from '@kfolks/types'
import { z } from 'zod'

/**
 * Zod schema for `request_types.workflow_config` (Spec 2).
 *
 * Wire-shape validator for the step-array workflow. Runs at `POST /request-types`
 * / `PATCH /request-types/:id` time. The engine assumes validated input at
 * runtime — all cross-step invariants (no duplicate step ids, `after.next`
 * targets exist, at least one approver dimension on approval/execution
 * steps) are enforced here so the engine can read configs without re-checking.
 *
 * `superRefine` is used for cross-step checks that Zod's object-level
 * validators can't express: references from one step into another, and
 * "at least one of N fields" where N is resolved from `.type`.
 */

// ─── shared ──────────────────────────────────────────────────────────────

const stepIdSchema = z
	.string()
	.min(1, 'step id required')
	.max(80, 'step id too long')
	.regex(/^[a-z0-9_-]+$/, 'step id must be lowercase alphanumeric, `_`, or `-`')

const slaHoursSchema = z
	.number()
	.int()
	.positive()
	.max(24 * 365)
	.optional()

const transitionGuardSchema = z.object({
	field: z.string().min(1),
	operator: z.enum(['equals', 'not_equals', 'contains', 'not_empty']),
	value: z.unknown().optional(),
})

const TERMINAL_NEXT = ['completed', 'rejected', 'cancelled'] as const

const stepTransitionSchema = z.object({
	on: z.enum(['success', 'failure']),
	guard: transitionGuardSchema.optional(),
	next: z.string().min(1),
})

// ─── approval ────────────────────────────────────────────────────────────

const skipIfSubmitterInSchema = z.object({
	people: z.array(z.uuid()).optional(),
	groups: z.array(z.string().min(1)).optional(),
})

const approvalStepSchema = z.object({
	id: stepIdSchema,
	type: z.literal('approval'),
	label: z.string().min(1).max(200),
	/**
	 * Sub-classification — UX label only, engine-equivalent. Defaults to
	 * `'decision'` when omitted (legacy parity).
	 */
	kind: z.enum(['decision', 'action']).optional(),
	/** Explicit employee UUIDs. */
	specificPeople: z.array(z.uuid()).optional(),
	/** Entra group IDs — any current member can act (live-resolved at inbox time). */
	groups: z.array(z.string().min(1)).optional(),
	/** When true, resolves `submitter.managerId` at step entry. */
	submittersManager: z.boolean().optional(),
	/**
	 * When true AND the request has `formData.collaborator_id` distinct
	 * from the submitter, resolves that employee's `managerId` at step
	 * entry. Wins over `submittersManager` if both resolve.
	 */
	collaboratorsManager: z.boolean().optional(),
	skipIfSubmitterIn: skipIfSubmitterInSchema.optional(),
	slaHours: slaHoursSchema,
	priorityTransform: z
		.object({
			to: z.enum(RequestPriority),
		})
		.optional(),
	after: stepTransitionSchema.optional(),
})

// ─── info_gate ───────────────────────────────────────────────────────────

const infoGateFieldSchema = z.object({
	id: z
		.string()
		.min(1)
		.max(80)
		.regex(/^[a-zA-Z0-9_]+$/, 'info-gate field id must be alphanumeric or `_`'),
	label: z.string().min(1).max(200),
	type: z.enum(['text', 'textarea', 'number', 'date', 'checkbox', 'select']),
	required: z.boolean().optional(),
	options: z
		.array(
			z.object({
				label: z.string().min(1),
				value: z.string().min(1),
			}),
		)
		.optional(),
})

const infoGateStepSchema = z.object({
	id: stepIdSchema,
	type: z.literal('info_gate'),
	label: z.string().min(1).max(200),
	mode: z.literal('manual'),
	fields: z.array(infoGateFieldSchema).min(1, 'info_gate step must declare at least one field'),
	slaHours: slaHoursSchema,
	after: stepTransitionSchema.optional(),
})

// ─── union + top-level ───────────────────────────────────────────────────

const workflowStepSchema = z.discriminatedUnion('type', [approvalStepSchema, infoGateStepSchema])

export const workflowConfigSchema = z
	.object({
		version: z.literal('1.0'),
		steps: z.array(workflowStepSchema).min(1, 'workflow must declare at least one step'),
	})
	.superRefine((cfg, ctx) => {
		const stepIds = new Set<string>()
		for (let i = 0; i < cfg.steps.length; i++) {
			const step = cfg.steps[i]

			// 1. unique step ids
			if (stepIds.has(step.id)) {
				ctx.addIssue({
					code: 'custom',
					path: ['steps', i, 'id'],
					message: `duplicate step id "${step.id}"`,
				})
			} else {
				stepIds.add(step.id)
			}

			// 2. approval steps need at least one approver dimension ticked-with-content
			if (step.type === 'approval') {
				const hasSpecificPeople = Array.isArray(step.specificPeople) && step.specificPeople.length > 0
				const hasGroups = Array.isArray(step.groups) && step.groups.length > 0
				const hasSubmittersManager = step.submittersManager === true
				const hasCollaboratorsManager = step.collaboratorsManager === true
				if (!hasSpecificPeople && !hasGroups && !hasSubmittersManager && !hasCollaboratorsManager) {
					ctx.addIssue({
						code: 'custom',
						path: ['steps', i],
						message: `approval step "${step.id}" requires at least one approver dimension (specificPeople, groups, submittersManager, or collaboratorsManager)`,
					})
				}
			}
		}

		// 3. `after.next` targets must exist (either a known step id or a terminal marker)
		for (let i = 0; i < cfg.steps.length; i++) {
			const step = cfg.steps[i]
			if (!step.after) continue
			const target = step.after.next
			const isTerminal = (TERMINAL_NEXT as readonly string[]).includes(target)
			if (!isTerminal && !stepIds.has(target)) {
				ctx.addIssue({
					code: 'custom',
					path: ['steps', i, 'after', 'next'],
					message: `unknown transition target "${target}"`,
				})
			}
		}
	})

export type WorkflowConfigInput = z.infer<typeof workflowConfigSchema>
